<template>
  <address class="tw-not-italic tw-space-y-2">
    <!-- Title -->
    <div class="fast-footer-title tw-text-mb-title-5 lg:tw-text-dt-title-5 tw-mb-4">
      {{ $t("global.contactUs") }}
    </div>

    <!-- Contact Items -->
    <div
      v-for="(item, index) in visibleContactItems"
      :key="`contact-item-${index}`"
      class="footer-contact-row tw-flex tw-items-start tw-text-mb-body-small lg:tw-text-dt-body-small"
      :class="{ 'tw-mb-2': item.addMargin }"
    >
      <QIcon
        :name="item.icon"
        class="footer-contact-icon tw-mt-1"
      />
      <div :class="['tw-min-w-0 tw-flex-1', item.containerClass]">
        <template v-if="item.type === 'text'">
          {{ item.content }}
        </template>

        <template v-else-if="item.type === 'link'">
          <ExternalLink
            :href="item.href || '#'"
            :title="item.title || ''"
            class="footer-contact-link link-underline-hover"
            @click="handleContactClick(item.eventType || '', item.eventValue || '')"
          >
            {{ item.content }}
          </ExternalLink>
        </template>

        <template v-else-if="item.type === 'phone-list'">
          <div class="footer-contact-phone-list">
            <ExternalLink
              v-for="phoneNumber in contact.phoneNumbers"
              :key="`phone-number-${phoneNumber}`"
              class="footer-contact-link footer-contact-phone-link link-underline-hover"
              :href="`tel:${phoneNumber}`"
              :title="$t('global.callNow')"
              @click="handleContactClick('phone', $phoneNumberFormat(phoneNumber))"
            >
              {{ $phoneNumberFormat(phoneNumber) }}
            </ExternalLink>
          </div>
        </template>
      </div>
    </div>
  </address>
</template>

<script setup lang="ts">
// Access Nuxt app and store
const { $phoneNumberFormat } = useNuxtApp()

// Contact information
const contact = useContactStore()

// Define all contact items in a single array for easier rendering
const contactItems = computed(() => [
  {
    type: 'text',
    icon: 'location_on',
    content: contact.fullAddress,
    show: !!contact.district,
    addMargin: true,
    containerClass: '',
  },
  {
    type: 'link',
    icon: 'mail',
    content: contact.email,
    href: contact.email ? `mailto:${contact.email}` : '#',
    title: 'Email',
    eventType: 'email',
    eventValue: contact.email || '',
    show: !!contact.email,
    addMargin: true,
    containerClass: '',
  },
  {
    type: 'phone-list',
    icon: 'call',
    show: contact.phoneNumbers && contact.phoneNumbers.length > 0,
    addMargin: false,
    containerClass: '',
  },
  {
    type: 'text',
    icon: 'fa-solid fa-clock',
    content: contact.officeHours,
    show: !!contact.officeHours,
    addMargin: false,
    containerClass: '',
  },
])

// Filter only items to be shown
const visibleContactItems = computed(() => contactItems.value.filter(item => item.show))

// Analytics event
const { analyticEvent, fbPixelEvent } = useAnalytic()

// Handle contact clicks for analytics
const handleContactClick = (social: string, contactValue: string) => {
  analyticEvent({
    ga4ActionName: `click_cta_footer_${social}`,
    ga4Label: contactValue,
  })

  fbPixelEvent({
    fbEvent: 'Contact',
  })
}
</script>

<style scoped>
/* Using standard CSS instead of @apply directive */
.fast-footer-contact {
  margin-top: 1rem;
  margin-bottom: 1rem;
}

.footer-contact-row {
  gap: 16px;
}

.footer-contact-icon {
  flex-shrink: 0;
}

.footer-contact-link {
  display: inline-block;
  width: fit-content;
  max-width: 100%;
}

.footer-contact-phone-list {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
}

.footer-contact-phone-link {
  display: block;
}

/* Override tw-space-y-2 margin-top on first contact item (after title) */
address > div:nth-child(2) {
  margin-top: 0px !important;
}
</style>
